let promoConnectSyncSuppressedUntil = 0;

function suppressPromoConnectSync(durationMs = 800) {
  promoConnectSyncSuppressedUntil = Date.now() + durationMs;
}

class MasterPromotion extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (typeof subscribe !== 'function') return;

    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event?.internal || event?.source === 'master-promotion') return;
      this.queuePromoUpdate(event?.cartData);
    });

    this.scheduleConnectSync();
  }

  scheduleConnectSync() {
    if (this._connectSyncScheduled) return;
    this._connectSyncScheduled = true;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this._connectSyncScheduled = false;
        if (Date.now() < promoConnectSyncSuppressedUntil) return;
        this.queuePromoUpdate();
      });
    });
  }

  queuePromoUpdate(cartData) {
    const run = () => {
      if (typeof PromoCartSync?.enqueuePromo === 'function') {
        PromoCartSync.enqueuePromo(() => this.updatePromoItems());
        return;
      }

      this.updatePromoItems();
    };

    if (this._promoUpdateCoalesceTimer) {
      clearTimeout(this._promoUpdateCoalesceTimer);
    }

    this._promoUpdateCoalesceTimer = setTimeout(() => {
      this._promoUpdateCoalesceTimer = null;
      run();
    }, 50);
  }

  disconnectedCallback() {
    if (this._promoUpdateCoalesceTimer) {
      clearTimeout(this._promoUpdateCoalesceTimer);
      this._promoUpdateCoalesceTimer = null;
    }

    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  getPromoData() {
    return JSON.parse(this.dataset.data || '{}');
  }

  getAutoAddProducts(promoData) {
    try {
      const fallbackProducts = JSON.parse(this.dataset.autoAddProducts || '[]');
      if (Array.isArray(fallbackProducts) && fallbackProducts.length > 0) {
        return fallbackProducts;
      }
    } catch (error) {
      /* fall through */
    }

    if (Array.isArray(promoData.product_list) && promoData.product_list.length > 0) {
      return promoData.product_list;
    }

    return [];
  }

  getUpgradeProducts(promoData) {
    try {
      const fallbackProducts = JSON.parse(this.dataset.upgradeProducts || '[]');
      if (Array.isArray(fallbackProducts) && fallbackProducts.length > 0) {
        return fallbackProducts;
      }
    } catch (error) {
      /* fall through */
    }

    if (Array.isArray(promoData.upgrade_product_list) && promoData.upgrade_product_list.length > 0) {
      return promoData.upgrade_product_list;
    }

    return [];
  }

  async getCart() {
    return fetch('/cart.js').then((response) => response.json());
  }

  isPromoLineItem(item, promoData) {
    if (item.properties?._promotional_item) return true;

    const variantId = item.variant_id || item.id;
    const rewardProducts = [...this.getAutoAddProducts(promoData), ...this.getUpgradeProducts(promoData)];

    return rewardProducts.some((product) => product.variants?.some((variant) => variant.id === variantId));
  }

  attachCollectionData(cart) {
    let attachedCount = 0;

    cart.items.forEach((item) => {
      if (this.isPromoLineItem(item, this.getPromoData())) {
        item.collections = [];
        return;
      }

      const onPageItem = document.querySelector('.cart-item-' + item.key.replace(':', '-'));
      if (!onPageItem) return;

      const collections = onPageItem.dataset.collections;
      if (!collections) return;

      try {
        item.collections = JSON.parse(collections);
        attachedCount += 1;
      } catch (error) {
        item.collections = [];
      }
    });

    return attachedCount;
  }

  getFirstAvailableProduct(productList) {
    if (!productList?.length) return null;

    return (
      productList.find((product) => product.variants?.some((variant) => variant.available)) ||
      productList[0]
    );
  }

  isUpgradeProductAvailable(productList) {
    if (!productList?.length) return false;

    return productList.some((product) => {
      const availableVariant = product.variants?.find((variant) => variant.available);
      if (!availableVariant) return false;
      return getCookie('deny_promo_' + availableVariant.id) !== 'true';
    });
  }

  isRestricted(promoData) {
    return this.dataset.restrictedActive === '1';
  }

  buildConditions(promoData, cart) {
    const conditions = [];

    for (let i = 1; i <= 3; i++) {
      if (promoData[`trigger_${i}_enabled`] === false) continue;

      const conditionCollection = promoData[`trigger_${i}_collection`];
      if (!conditionCollection) continue;

      const conditionItemsInCart = cart.items
        .filter(
          (item) =>
            !item.properties?._promotional_item &&
            Array.isArray(item.collections) &&
            item.collections.includes(conditionCollection) &&
            !item.title.includes('Big')
        )
        .reduce((total, item) => total + item.quantity, 0);

      conditions.push({
        index: i,
        collection: conditionCollection,
        qtyNeeded: promoData[`trigger_${i}_qty`],
        upgradeQty: promoData.upgrade_enabled === false ? 0 : promoData[`trigger_${i}_upgrade_qty`] || 0,
        count: conditionItemsInCart,
      });
    }

    return conditions;
  }

  isUpgradeConfigured(promoData) {
    if (promoData.upgrade_enabled === false) return false;

    const upgradeProducts = this.getUpgradeProducts(promoData);
    if (upgradeProducts.length > 0) return true;

    return (
      Array.isArray(promoData.upgrade_product_list) && promoData.upgrade_product_list.length > 0
    );
  }

  calculateCycleBasePromoCount(count, baseThreshold, upgradeThreshold, baseOnlyQty, fullCycleBaseQty, fullCycleSize) {
    if (count < baseThreshold) return 0;

    // When no upgrade threshold is configured, fall back to simple base spacing.
    if (!upgradeThreshold || upgradeThreshold <= 0) {
      return Math.floor(count / baseThreshold) * baseOnlyQty;
    }

    // Base kits are earned inside each upgrade-sized window, not accumulated per
    // completed full cycle. With base=3 and upgrade=5:
    //   remainders 3–4 → 1 col-kit (e.g. 3–4, 8–9, 13–14, 18–19, 23–24)
    //   remainders 0–2 → upgrade-only / no base (e.g. 5–7, 10–12, 15–17, 20–22, 25)
    // fullCycleSize is retained for callers/settings compatibility but base placement
    // is driven by upgradeThreshold so 15–25 match the Halloween kit schedule.
    const rem = count % upgradeThreshold;
    if (rem >= baseThreshold) {
      return fullCycleBaseQty || baseOnlyQty;
    }

    return 0;
  }

  calculatePromoCounts(promoData, conditions) {
    let basePromoCount = 0;
    let upgradePromoCount = 0;

    const allConditionsMet = conditions.every((condition) => condition.count >= condition.qtyNeeded);
    if (!allConditionsMet) {
      return { basePromoCount, upgradePromoCount };
    }

    const upgradeCondition = conditions.find((condition) => condition.upgradeQty > 0);
    const allocationCondition = upgradeCondition || conditions[0];
    const baseThreshold = parseInt(allocationCondition.qtyNeeded, 10) || 1;
    const upgradeThreshold = parseInt(allocationCondition.upgradeQty, 10) || 0;
    const baseOnlyQty = parseInt(promoData.product_qty, 10) || 1;
    const upgradeOnlyQty = parseInt(promoData.upgrade_product_qty, 10) || 1;
    const fullCycleBaseQty = parseInt(promoData.promo_full_cycle_base_qty, 10) || baseOnlyQty;
    const count = allocationCondition.count;

    const upgradeProducts = this.getUpgradeProducts(promoData);
    const upgradeConfigured = this.isUpgradeConfigured(promoData);
    const hasUpgradeThresholds = conditions.some((condition) => condition.upgradeQty > 0);
    const upgradeStockAvailable =
      upgradeProducts.length > 0 && this.isUpgradeProductAvailable(upgradeProducts);
    const configuredFullCycle = parseInt(promoData.promo_full_cycle_qty, 10);
    const fullCycleSize =
      configuredFullCycle ||
      upgradeThreshold + baseThreshold + Math.max(0, upgradeThreshold - baseThreshold);

    if (upgradeConfigured && hasUpgradeThresholds) {
      // Always use the cycle table for base rewards when an upgrade tier exists.
      // The simple floor(count / 3) fallback is only for confirmed sold-out upgrades.
      basePromoCount = this.calculateCycleBasePromoCount(
        count,
        baseThreshold,
        upgradeThreshold,
        baseOnlyQty,
        fullCycleBaseQty,
        fullCycleSize
      );

      if (upgradeStockAvailable && count >= upgradeThreshold) {
        upgradePromoCount = Math.floor(count / upgradeThreshold) * upgradeOnlyQty;
      } else if (!upgradeStockAvailable && upgradeProducts.length > 0) {
        basePromoCount = Math.floor(count / baseThreshold) * baseOnlyQty;
        upgradePromoCount = 0;
      }
    } else {
      basePromoCount = Math.floor(count / baseThreshold) * baseOnlyQty;
    }

    const baseMaxQty = parseInt(promoData.product_max_qty, 10) || 0;
    if (baseMaxQty > 0) {
      basePromoCount = Math.min(basePromoCount, baseMaxQty);
    }

    const upgradeMaxQty = parseInt(promoData.upgrade_max_qty, 10) || 0;
    if (upgradeMaxQty > 0) {
      upgradePromoCount = Math.min(upgradePromoCount, upgradeMaxQty);
    }

    return { basePromoCount, upgradePromoCount };
  }

  getCartDrawer() {
    return document.querySelector('cart-drawer');
  }

  getSectionsToRender() {
    const sections = [
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'CartDrawer',
        selector: '.drawer__inner',
        section: 'cart-drawer',
      },
    ];

    if (window.request?.page_type === 'cart') {
      const mainCartItems = document.getElementById('main-cart-items');
      if (mainCartItems?.dataset.id) {
        sections.push({
          id: 'main-cart-items',
          section: mainCartItems.dataset.id,
          selector: '.js-contents',
        });
      }

      sections.push({
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      });
    }

    document.querySelectorAll('promotional-drawer').forEach((drawer) => {
      sections.push({
        id: 'PromotionHolder',
        section: 'cart-drawer',
        selector: '#PromotionDrawerFooter-' + drawer.dataset.promoId,
      });
    });

    return sections;
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(lines = []) {
    const mainCartItems =
      document.getElementById('CartDrawer-CartItems') || document.getElementById('main-cart-items');
    mainCartItems?.classList.add('cart__items--disabled');

    lines.forEach((line) => {
      const cartItemElements = document.querySelectorAll(`#CartItem-${line} .loading__spinner`);
      const cartDrawerItemElements = document.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);
      [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));
    });
  }

  disableLoading(lines = []) {
    lines.forEach((line) => {
      const cartItemElements = document.querySelectorAll(`#CartItem-${line} .loading__spinner`);
      const cartDrawerItemElements = document.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);
      cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
      cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    });

    document.querySelectorAll('cart-drawer-items .loading__spinner, cart-items .loading__spinner').forEach((overlay) => {
      overlay.classList.add('hidden');
    });

    if (PromoCartSync?.isLocked()) return;

    const mainCartItems =
      document.getElementById('CartDrawer-CartItems') || document.getElementById('main-cart-items');
    mainCartItems?.classList.remove('cart__items--disabled');
  }

  runPostSyncTasks() {
    setTimeout(() => {
      document.querySelectorAll('promotional-drawer').forEach((drawer) => drawer.setDrawerOpeningLinks());
    });
  }

  renderUpdatedSections(parsedState) {
    const cartDrawerWrapper = document.querySelector('cart-drawer');
    const cartItemsElement = document.querySelector('cart-drawer-items') || document.querySelector('cart-items');

    cartItemsElement?.classList.toggle('is-empty', parsedState.item_count === 0);
    cartDrawerWrapper?.classList.toggle('is-empty', parsedState.item_count === 0);

    if (window.request?.page_type === 'cart') {
      document.getElementById('main-cart-items')?.classList.toggle('is-empty', parsedState.item_count === 0);
      document.getElementById('main-cart-footer')?.classList.toggle('is-empty', parsedState.item_count === 0);
    }

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = document.getElementById(section.id);
      if (!sectionElement || !parsedState.sections?.[section.section]) return;

      const elementToReplace = sectionElement.querySelector(section.selector) || sectionElement;
      elementToReplace.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.section],
        section.selector
      );

      if (typeof logCartDomReplace === 'function') {
        logCartDomReplace('promo-auto-add:renderUpdatedSections', section.selector || section.id);
      }
    });
  }

  addItems(items, options = {}) {
    const { render = true, publish = true, includeSections = true } = options;
    const cart = this.getCartDrawer();
    if (!cart) return Promise.resolve(null);

    const payload = { items };

    if (includeSections) {
      payload.sections = cart.getSectionsToRender().map((section) => section.id);
      payload.sections_url = window.location.pathname;
    }

    const body = JSON.stringify(payload);

    return fetch(`${routes.cart_add_url}`, { ...fetchConfig('javascript'), body })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'master-promotion',
            productVariantId: items[0].id,
            errors: response.errors || response.description,
            message: response.message,
          });
          return null;
        }

        if (publish) {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'master-promotion',
            productVariantId: items[0].id,
            cartData: response,
          });
        }

        if (render) {
          cart.renderContents(response);
        }

        return response;
      })
      .catch((error) => {
        console.error(error);
        return null;
      });
  }

  updateMultipleQty(items, lines, options = {}) {
    const { render = true, publish = true, includeSections = true } = options;
    const cart = this.getCartDrawer();
    if (!cart) return Promise.resolve(null);

    const payload = { updates: items };

    if (includeSections) {
      payload.sections = this.getSectionsToRender().map((section) => section.section);
      payload.sections_url = window.location.pathname;
    }

    const body = JSON.stringify(payload);

    return fetch(`${routes.cart_update_url}`, { ...fetchConfig(), body })
      .then((response) => response.text())
      .then((state) => {
        const parsedState = JSON.parse(state);

        if (publish) {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'master-promotion',
            cartData: parsedState,
          });
        }

        if (render) {
          this.renderUpdatedSections(parsedState);
        }

        return parsedState;
      })
      .catch((error) => {
        console.error(error);
        cart.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        if (errors) errors.textContent = window.cartStrings.error;
        return null;
      })
      .finally(() => {
        if (render) {
          this.runPostSyncTasks();
          this.disableLoading(lines);
        }
      });
  }

  buildPromoSyncPlan(promoData, cart) {
    const nonPromoLineItems = cart.items.filter((item) => !this.isPromoLineItem(item, promoData));
    const attachedCount = this.attachCollectionData(cart);

    if (nonPromoLineItems.length > 0 && attachedCount < nonPromoLineItems.length) {
      return null;
    }

    const conditions = this.buildConditions(promoData, cart);
    if (!conditions.length) return null;

    const triggerCount = conditions.reduce((max, condition) => Math.max(max, condition.count), 0);

    if (triggerCount === 0 && nonPromoLineItems.length > 0) {
      return null;
    }

    const { basePromoCount, upgradePromoCount } = this.calculatePromoCounts(promoData, conditions);
    const itemsToUpdate = {};
    const itemsToAdd = [];
    const lines = [];

    if (promoData.payload_enabled !== false) {
      const baseProduct = this.getFirstAvailableProduct(this.getAutoAddProducts(promoData));
      if (baseProduct) {
        this.syncPromoProduct(baseProduct, basePromoCount, cart, itemsToUpdate, itemsToAdd, lines);
      }
    }

    if (promoData.upgrade_enabled !== false) {
      const upgradeProduct = this.getFirstAvailableProduct(this.getUpgradeProducts(promoData));
      if (upgradeProduct) {
        this.syncPromoProduct(upgradeProduct, upgradePromoCount, cart, itemsToUpdate, itemsToAdd, lines);
      }
    }

    return { itemsToUpdate, itemsToAdd, lines };
  }

  async applyPromoCartChanges(plan, promoData) {
    let itemsToUpdate = { ...plan.itemsToUpdate };
    let itemsToAdd = [...plan.itemsToAdd];
    let lines = [...plan.lines];
    const hasUpdates = Object.keys(itemsToUpdate).length > 0;
    const hasAdds = itemsToAdd.length > 0;

    if (!hasUpdates && !hasAdds) return null;

    suppressPromoConnectSync();

    this.enableLoading(lines);

    let parsedState = null;

    if (hasUpdates) {
      parsedState = await this.updateMultipleQty(itemsToUpdate, lines, {
        render: false,
        publish: false,
        includeSections: true,
      });
    }

    let didAddItems = false;

    if (hasAdds) {
      if (hasUpdates) {
        const cartAfterUpdates = parsedState?.items ? parsedState : await this.getCart();
        const recalculatedPlan = this.buildPromoSyncPlan(promoData, cartAfterUpdates);
        itemsToAdd = recalculatedPlan?.itemsToAdd || [];
        itemsToUpdate = recalculatedPlan?.itemsToUpdate || {};
        lines = recalculatedPlan?.lines || lines;

        if (Object.keys(itemsToUpdate).length > 0) {
          parsedState = await this.updateMultipleQty(itemsToUpdate, lines, {
            render: false,
            publish: false,
            includeSections: true,
          });
        }
      }

      if (itemsToAdd.length > 0) {
        parsedState = await this.addItems(itemsToAdd, {
          render: false,
          publish: false,
          includeSections: true,
        });
        didAddItems = true;
      }
    }

    if (!parsedState) return null;

    if (didAddItems && parsedState.sections) {
      this.getCartDrawer()?.renderContents(parsedState);
    } else {
      this.renderUpdatedSections(parsedState);
    }

    PromoCartSync?.refreshLockUI();

    this.runPostSyncTasks();
    this.disableLoading(lines);

    return parsedState;
  }

  syncPromoProduct(product, targetQty, cart, itemsToUpdate, itemsToAdd, lines) {
    if (!product?.variants?.length) return;

    const productVariantIds = product.variants.map((variant) => variant.id);
    const availableVariant = product.variants.find((variant) => variant.available);
    const promoCartItem = cart.items.filter((item) => productVariantIds.includes(item.id));
    const promoCartItemIndex = cart.items.findIndex((item) => productVariantIds.includes(item.id));
    const promoQtyInCart = promoCartItem.length > 0 ? promoCartItem[0].quantity : 0;

    if (promoCartItem.length > 0 && promoQtyInCart !== targetQty) {
      itemsToUpdate[promoCartItem[0].key] = targetQty;
      lines.push(promoCartItemIndex + 1);
      return;
    }

    if (!availableVariant || targetQty <= 0 || promoQtyInCart === targetQty) return;
    if (getCookie('deny_promo_' + availableVariant.id) === 'true') return;

    itemsToAdd.push({
      id: availableVariant.id,
      quantity: targetQty,
      properties: {
        _promotional_item: true,
        _promotional_item_type: 'percentage_off',
        _promotional_item_amount: '100',
        _promotional_item_message: 'Special Offer',
      },
    });
  }

  async updatePromoItems() {
    const promoData = this.getPromoData();

    if (promoData.promotion_presentation_type !== 'auto_add') return;
    if (this.isRestricted(promoData)) return;

    try {
      const cart = await this.getCart();
      if (!cart?.items?.length) return;

      const plan = this.buildPromoSyncPlan(promoData, cart);
      if (!plan) return;

      const hasChanges = plan.itemsToAdd.length > 0 || Object.keys(plan.itemsToUpdate).length > 0;

      if (hasChanges) {
        await this.applyPromoCartChanges(plan, promoData);
      } else if (typeof scheduleRefreshStrikethrough === 'function') {
        scheduleRefreshStrikethrough('master-promotion:updatePromoItems-no-change');
      }
    } catch (error) {
      console.error(error);
    }
  }
}

if (!customElements.get('master-promotion')) {
  customElements.define('master-promotion', MasterPromotion);
}
